import { ChangeDetectorRef, OnInit, signal } from '@angular/core';
import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { lastValueFrom } from 'rxjs';
import { Project } from 'src/app/models/project.model';
import { AccessRequest } from 'src/app/models/membership.model';
import { AuthService } from 'src/app/services/auth.service';
import { MembershipService } from 'src/app/services/membership.service';
import { ProjectManagementService } from 'src/app/services/project-management.service';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  standalone: false
})
export class DashboardPageComponent implements OnInit {

  projects: Project[] = [];
  loading = true;
  query = '';
  isAdmin = this.authService.hasAdminPreference();
  /** Members only see their teams' projects, so an empty list means "no team yet", not "no projects". */
  readonly isMember = signal(false);
  /** People who opened a link into this organization and asked to join. */
  readonly accessRequests = signal<AccessRequest[]>([]);

  constructor(
    private router: Router,
    private authService: AuthService,
    private projectManagementService: ProjectManagementService,
    private membershipService: MembershipService,
    private cdr: ChangeDetectorRef
  ) { }

  async ngOnInit() {
    lastValueFrom(this.membershipService.myRole()).then(role => this.isMember.set(role === 'member'));
    // Not awaited: the notice is secondary and must not hold up the project list.
    lastValueFrom(this.membershipService.getAccessRequests())
      .then(requests => this.accessRequests.set(requests))
      .catch(() => this.accessRequests.set([]));
    await this.getProjects()
  }

  logout() {
    this.authService.logout()
  }

  async getProjects() {
    this.loading = true;
    this.cdr.markForCheck();
    this.projects = await lastValueFrom(this.projectManagementService.getProjects());
    this.loading = false;
    this.cdr.markForCheck()
  }

  get filteredProjects(): Project[] {
    const q = this.query.trim().toLowerCase();
    if (!q) return this.projects;
    return this.projects.filter(p =>
      p.name.toLowerCase().includes(q) || (p.status_page_slug ?? '').toLowerCase().includes(q)
    );
  }

  relativeTime(value?: string): string | null {
    if (!value) return null;
    const then = new Date(value).getTime();
    if (isNaN(then)) return null;
    const seconds = Math.round((Date.now() - then) / 1000);
    const units: [Intl.RelativeTimeFormatUnit, number][] = [
      ['year', 31536000], ['month', 2592000], ['week', 604800],
      ['day', 86400], ['hour', 3600], ['minute', 60],
    ];
    const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
    for (const [unit, size] of units) {
      if (seconds >= size) return rtf.format(-Math.floor(seconds / size), unit);
    }
    return 'just now';
  }

  addNewProject() {
    this.router.navigate(['/main', 'new-project']);
  }

}
